type ItemExample = { title: string; description: string; photos: string };

// [posting.examples-and-suggestions](../../../docs/product/marketplace.md#posting-examples-and-suggestions): every selectable item ID has its own guidance. These are
// illustrative details, never catalog rules or values copied into the draft.
export const ITEM_POSTING_EXAMPLES: Record<string, ItemExample> = {
  tools: {
    title: "e.g. Used 18 V cordless drill with battery and charger",
    description: "e.g. 18 V cordless drill used for 1 year, with one battery, charger and case. Describe the brand, model, working condition, visible wear and price per set.",
    photos: "Examples: the actual drill, model label, battery, charger and visible wear.",
  },
  heavy_machinery: {
    title: "e.g. Used 3-ton diesel forklift – 2,400 operating hours",
    description: "e.g. 3-ton diesel forklift with 2,400 operating hours. State the model, lifting capacity, maintenance history, known issues, included attachments and loading arrangements.",
    photos: "Examples: the actual forklift, capacity plate, hour meter, mast and attachments.",
  },
  industrial_equipment: {
    title: "e.g. Used 2 HP industrial air compressor with tank",
    description: "e.g. 2 HP air compressor with a 100 L tank for workshop use. State the brand, voltage, pressure rating, service history, included fittings and known faults.",
    photos: "Examples: the compressor, motor plate, tank, gauges and included fittings.",
  },
  restaurant_equipment: {
    title: "e.g. Stainless commercial gas range with 4 burners",
    description: "e.g. Four-burner commercial gas range used in a restaurant kitchen. State the dimensions, fuel type, burner condition, included parts and cleaning or repair needs.",
    photos: "Examples: the actual range, burners, controls, interior and product label.",
  },
  electronics: {
    title: "e.g. Used office laser printer with duplex printing",
    description: "e.g. Laser printer with duplex printing and network connection. State the brand, model, page count, toner condition, included cables and any known faults.",
    photos: "Examples: the printer, model label, ports, sample print and included cables.",
  },
  appliances: {
    title: "e.g. 10 cu. ft. inverter refrigerator for office use",
    description: "e.g. 10 cu. ft. inverter refrigerator with two compartments. State the brand, model, condition, dimensions, voltage, included accessories and any remaining warranty.",
    photos: "Examples: the actual refrigerator, interior, model label and included accessories.",
  },
  furniture: {
    title: "e.g. Office desk with drawers – 120 × 60 cm",
    description: "e.g. 120 × 60 cm office desk with three drawers. Describe the material, condition, assembly requirements, visible marks and pickup arrangements.",
    photos: "Examples: the desk, drawers, tabletop, dimensions and any damaged areas.",
  },
  agricultural_equipment: {
    title: "e.g. Used walk-behind rice transplanter",
    description: "e.g. Walk-behind rice transplanter used for two planting seasons. State the brand, model, row count, engine condition, service history and included spare parts.",
    photos: "Examples: the transplanter, engine, planting mechanism and model plate.",
  },
  event_equipment: {
    title: "e.g. Portable PA speaker pair with stands",
    description: "e.g. Two powered PA speakers with stands for events. State the brand, model, power rating, inputs, working condition and included cables or cases.",
    photos: "Examples: both speakers, input panels, stands, cables and cases.",
  },
  electrical_equipment: {
    title: "e.g. Used 5 kVA portable generator",
    description: "e.g. 5 kVA portable generator for site backup power. State the model, fuel type, output voltage, operating hours, maintenance history and known issues.",
    photos: "Examples: the generator, rating plate, outlets, hour meter and controls.",
  },
  formwork_scaffold: {
    title: "e.g. Steel scaffolding frames with cross braces",
    description: "e.g. Steel scaffolding frames with cross braces for sale. State the dimensions, frame count, included parts, visible wear and collection arrangements.",
    photos: "Examples: frames, braces, connections, included parts and visible condition.",
  },
  site_facilities: {
    title: "e.g. Used portable site office – 20 ft",
    description: "e.g. 20 ft portable site office with a door, windows and electrical fittings. Describe its dimensions, condition, included fixtures and transport or loading arrangements.",
    photos: "Examples: the actual office exterior, interior, fixtures and access points.",
  },
  equipment: {
    title: "e.g. Workshop bench grinder with adjustable tool rests",
    description: "e.g. Bench grinder for workshop use with adjustable tool rests. State its brand, model, wheel size, voltage, condition, included parts and price.",
    photos: "Examples: the actual equipment, product label, controls and included parts.",
  },
  material: {
    title: "e.g. 40 kg Portland cement bags for sale",
    description: "e.g. Unopened 40 kg Portland cement bags stored in a dry warehouse. State the brand, available quantity, price per bag and pickup or delivery arrangements.",
    photos: "Examples: the actual cement stock, bag labels and storage condition.",
  },
  electrical: {
    title: "e.g. Unused 20 A circuit breakers",
    description: "e.g. Unused 20 A circuit breakers in original packaging. State the brand, model, pole count, voltage rating, quantity and price per piece.",
    photos: "Examples: the circuit breakers, rating labels and packaging.",
  },
  plumbing_sanitary: {
    title: "e.g. 1-inch PVC pipes – 3 m lengths",
    description: "e.g. Unused 1-inch PVC pipes in 3 m lengths. State the brand, pressure rating, available quantity, price per length and pickup arrangements.",
    photos: "Examples: the pipes, printed specifications, dimensions and fittings offered.",
  },
  safety_ppe: {
    title: "e.g. Construction safety helmets with adjustable fit",
    description: "e.g. Adjustable safety helmets in sealed packaging. State the brand, model, stated safety rating, manufacturing date, quantity and price per helmet.",
    photos: "Examples: the helmets, suspension system, product labels and packaging.",
  },
  uniforms: {
    title: "e.g. Work polo uniforms – assorted sizes",
    description: "e.g. Work polo uniforms in sizes S to XL. State the fabric, colors, size measurements, available quantity, customization options and price per shirt.",
    photos: "Examples: the actual uniforms, front and back, fabric detail and size labels.",
  },
  safety_footwear: {
    title: "e.g. Steel-toe safety boots – size 42",
    description: "e.g. Size 42 steel-toe safety boots in original packaging. State the brand, stated safety rating, upper and sole materials, condition and price per pair.",
    photos: "Examples: both boots, soles, toe area, size label and packaging.",
  },
  farm_products: {
    title: "e.g. Freshly harvested tomatoes – sold per kilogram",
    description: "e.g. Fresh tomatoes harvested this week, available in 10 kg crates. State the variety, grade, harvest date, available weight, price per kilogram and collection arrangements.",
    photos: "Examples: the actual produce, size and quality, crates and packaging.",
  },
  agricultural_supplies: {
    title: "e.g. Vegetable seed packets for planting",
    description: "e.g. Sealed vegetable seed packets for planting. State the crop variety, brand, packet weight, lot or expiry details, available quantity and price per packet.",
    photos: "Examples: the seed packets, variety labels, weight and expiry information.",
  },
  fixtures_finishes: {
    title: "e.g. 60 × 60 cm porcelain floor tiles",
    description: "e.g. Unopened boxes of 60 × 60 cm porcelain floor tiles. State the brand, color, finish, batch, coverage per box, available quantity and price per box.",
    photos: "Examples: tile finish, actual boxes, dimensions and batch labels.",
  },
  surplus_salvaged: {
    title: "e.g. Salvaged steel beams in assorted lengths",
    description: "e.g. Salvaged steel beams in assorted lengths. State the sizes, available quantity or weight, visible rust or damage, price basis and pickup arrangements.",
    photos: "Examples: the actual beams, length measurements, rust and damaged areas.",
  },
  consumables: {
    title: "e.g. Packing tape rolls – 48 mm × 100 m",
    description: "e.g. Clear packing tape rolls measuring 48 mm × 100 m. State the brand, thickness, rolls per carton, available stock, minimum order and price per roll or carton.",
    photos: "Examples: tape rolls, carton packaging, dimensions and product labels.",
  },
  cars: {
    title: "e.g. Used compact sedan – automatic, 65,000 km",
    description: "e.g. Compact sedan with automatic transmission and 65,000 km mileage. State the make, model, year, fuel type, service history, registration status and known issues.",
    photos: "Examples: the car exterior, cabin, dashboard mileage, engine bay and visible damage.",
  },
  motorcycles: {
    title: "e.g. Used 125 cc commuter motorcycle",
    description: "e.g. 125 cc motorcycle used for daily commuting. State the make, model, year, mileage, registration status, maintenance history and included accessories.",
    photos: "Examples: both sides of the motorcycle, odometer, tires and engine area.",
  },
  trucks: {
    title: "e.g. Used 6-wheel cargo truck with enclosed body",
    description: "e.g. Six-wheel cargo truck with an enclosed body. State the make, year, mileage, payload, cargo dimensions, service history, registration status and known issues.",
    photos: "Examples: truck exterior, cargo body, cabin, tires and odometer.",
  },
  vans: {
    title: "e.g. Used diesel delivery van – manual transmission",
    description: "e.g. Diesel delivery van with manual transmission and 85,000 km mileage. State the year, cargo dimensions, maintenance history, registration status and inspection arrangements.",
    photos: "Examples: van exterior, cabin, cargo space, odometer and visible wear.",
  },
  buses: {
    title: "e.g. Used 30-seat shuttle bus",
    description: "e.g. Thirty-seat shuttle bus for sale. State the make, year, mileage, air-conditioning condition, seating layout, service history and registration status.",
    photos: "Examples: bus exterior, seats, driver area, luggage space and odometer.",
  },
  boats: {
    title: "e.g. Fiberglass fishing boat with outboard motor",
    description: "e.g. Fiberglass fishing boat with an outboard motor. State the hull length, engine make and power, engine hours, hull condition, included equipment and available documents.",
    photos: "Examples: the hull, deck, outboard motor, identification plate and included equipment.",
  },
  trailers: {
    title: "e.g. Used utility cargo trailer with steel frame",
    description: "e.g. Steel-frame utility cargo trailer. State the bed dimensions, load rating, axle count, coupling type, tire and brake condition and available documents.",
    photos: "Examples: trailer bed, frame, coupling, axles, tires and rating plate.",
  },
  vehicle_parts: {
    title: "e.g. Used alloy wheel set – 15 inch, 4 pieces",
    description: "e.g. Set of four 15-inch alloy wheels. State the bolt pattern, offset, compatible vehicle models, condition, known repairs and whether tires are included.",
    photos: "Examples: all wheels, size markings, mounting points and damaged areas.",
  },
  vehicle_transport: {
    title: "e.g. Used electric cargo tricycle",
    description: "e.g. Electric cargo tricycle for local deliveries. State the model, payload, battery type and age, charger inclusions, condition and available registration documents.",
    photos: "Examples: the actual vehicle, cargo area, battery, charger and model plate.",
  },
  commercial_spaces: {
    title: "e.g. 50 m² ground-floor retail space for sale",
    description: "e.g. 50 m² ground-floor retail space with street frontage. State the floor area, access, utilities, current occupancy, included fixtures and viewing arrangements.",
    photos: "Examples: storefront, interior, access and fixtures included in the sale.",
  },
  offices: {
    title: "e.g. 80 m² commercial office for sale in Makati",
    description: "e.g. 80 m² office with two rooms, a pantry and one parking slot. State the floor, access, current occupancy, included features and viewing arrangements.",
    photos: "Examples: actual office rooms, pantry, exterior and parking access.",
  },
  warehouses: {
    title: "e.g. 500 m² warehouse with loading access",
    description: "e.g. 500 m² warehouse with truck loading access. State the ceiling height, floor area, loading doors, power supply, current occupancy and viewing arrangements.",
    photos: "Examples: warehouse interior, roof, loading doors, access road and exterior.",
  },
  industrial_properties: {
    title: "e.g. Factory property with 1,200 m² production building",
    description: "e.g. Industrial property with a 1,200 m² production building. State the lot and floor areas, utility capacity, access, existing structures, occupancy and sale inclusions.",
    photos: "Examples: production building, lot, utility areas, access and existing structures.",
  },
  lots: {
    title: "e.g. 2,000 m² agricultural lot with road access",
    description: "e.g. 2,000 m² agricultural lot with road access. State the location, area, terrain, current use, utility access, available ownership documents and viewing arrangements.",
    photos: "Examples: the actual land, boundaries, terrain and road access.",
  },
  property: {
    title: "e.g. Commercial building with shop and storage space",
    description: "e.g. Commercial building with a shop and storage room. State the lot and floor areas, access, utilities, occupancy, condition and structures included in the asking price.",
    photos: "Examples: building exterior, shop, storage area and access points.",
  },
  restaurant_business: {
    title: "e.g. Operating neighborhood café business for sale",
    description: "e.g. Operating café business offered with listed kitchen equipment and furniture. Describe the operating status, included assets, premises arrangement, stock, exclusions and handover terms.",
    photos: "Examples: café premises, seating, kitchen and assets included in the sale.",
  },
  retail_business: {
    title: "e.g. Operating hardware retail business for sale",
    description: "e.g. Hardware retail business with listed inventory and display fixtures. Describe the operating status, included stock, assets, premises arrangement, exclusions and proposed handover.",
    photos: "Examples: retail premises, shelves, stock and included fixtures.",
  },
  construction_business: {
    title: "e.g. Renovation contracting business for sale",
    description: "e.g. Renovation contracting business offered with listed tools and vehicles. Describe its operating status, included assets, office arrangement, ongoing commitments, exclusions and handover terms.",
    photos: "Examples: office, workshop, tools and vehicles included in the business sale.",
  },
  manufacturing_business: {
    title: "e.g. Furniture manufacturing business for sale",
    description: "e.g. Furniture manufacturing business with listed machines and stock. Describe the operating status, production assets, premises arrangement, inventory, exclusions and proposed handover.",
    photos: "Examples: production area, machinery, stock and included business premises.",
  },
  transport_business: {
    title: "e.g. Local delivery and logistics business for sale",
    description: "e.g. Local delivery business with a listed vehicle fleet. Describe the operating status, included vehicles and equipment, depot arrangement, existing commitments, exclusions and handover terms.",
    photos: "Examples: included fleet, depot, delivery equipment and office.",
  },
  farm_business: {
    title: "e.g. Operating vegetable farm business for sale",
    description: "e.g. Vegetable farm business with listed irrigation equipment and tools. Describe the operating status, land arrangement, crops, included assets, exclusions and seasonal handover considerations.",
    photos: "Examples: farm operations, irrigation, tools and assets included in the business sale.",
  },
  service_business: {
    title: "e.g. Operating laundry service business for sale",
    description: "e.g. Laundry service business with listed washers and dryers. Describe the operating status, machines, premises arrangement, supplies, existing commitments, exclusions and handover terms.",
    photos: "Examples: laundry premises, washers, dryers and included equipment.",
  },
  business: {
    title: "e.g. Operating printing business for sale",
    description: "e.g. Printing business offered with listed machines and supplies. Describe its operating status, included assets, premises arrangement, commitments, exclusions and proposed handover.",
    photos: "Examples: business premises, printing equipment, supplies and assets included in the sale.",
  },
};
